import React, { useEffect, useMemo, useState } from "react";
import { X, Upload, Check, AlertCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "react-toastify";
import { previewImport, startImport } from "../../../apis/contactImports";
import { listContactLists } from "../../../apis/contactLists";
import { DEFAULT_CONTACT_FIELDS, SKIP_TARGET } from "../../../utils/contactDefaultFields";

const STEPS = [
  { id: 1, label: "Upload" },
  { id: 2, label: "Map fields" },
  { id: 3, label: "Confirm" },
];

const ImportWizard = ({ phoneId, onClose, onComplete }) => {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [lists, setLists] = useState([]);
  const [listId, setListId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!phoneId) return;
    listContactLists({ phone_id: phoneId, limit: 200, type: "static" })
      .then((r) => setLists(r.data.lists || []))
      .catch(() => {});
  }, [phoneId]);

  const handleFile = (f) => {
    setFile(f);
    setPreview(null);
    setMapping({});
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const runPreview = async () => {
    if (!file) {
      toast.error("Choose a CSV first");
      return;
    }
    setPreviewing(true);
    try {
      const r = await previewImport(file, phoneId);
      setPreview(r.data);
      setMapping(r.data.mapping || {});
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.error || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const targetOptions = useMemo(() => {
    const defaults = DEFAULT_CONTACT_FIELDS.map((f) => ({
      value: f.key,
      label: `${f.label}${f.required ? " *" : ""}`,
      group: "Default",
    }));
    const customs = (preview?.custom_fields || []).map((cf) => ({
      value: `custom.${cf.field_key}`,
      label: `${cf.label} (custom)`,
      group: "Custom",
    }));
    return { defaults, customs };
  }, [preview]);

  const phoneIsMapped = useMemo(
    () => Object.values(mapping).includes("phone"),
    [mapping]
  );

  const submit = async () => {
    if (!phoneIsMapped) {
      toast.error("Map at least one CSV column to `phone`");
      return;
    }
    setSubmitting(true);
    try {
      const r = await startImport({
        file,
        phone_id: phoneId,
        list_id: listId ? parseInt(listId, 10) : null,
        mapping,
      });
      if (r.data.job_id) {
        onComplete && onComplete(r.data.job_id);
      } else {
        toast.error("Unexpected response");
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Import failed to start");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Import contacts</h3>
              <p className="text-xs text-gray-500 mt-0.5">CSV up to 100 MB. Processed in the background.</p>
            </div>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100"><X size={18} /></button>
          </div>

          <div className="px-5 py-3 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-3">
              {STEPS.map((s, i) => {
                const done = step > s.id;
                const active = step === s.id;
                return (
                  <React.Fragment key={s.id}>
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                        done ? "bg-emerald-600 text-white" : active ? "bg-emerald-50 text-emerald-700 border border-emerald-300" : "bg-gray-200 text-gray-600"
                      }`}>
                        {done ? <Check size={12} /> : s.id}
                      </div>
                      <span className={`text-xs font-medium ${active ? "text-emerald-700" : done ? "text-gray-700" : "text-gray-500"}`}>
                        {s.label}
                      </span>
                    </div>
                    {i < STEPS.length - 1 && <div className="flex-1 h-px bg-gray-200" />}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-5">
            {step === 1 && (
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center bg-gray-50"
              >
                <Upload size={36} className="mx-auto text-gray-400" />
                <p className="mt-2 text-sm font-medium text-gray-900">Drop your CSV here</p>
                <p className="text-xs text-gray-500">or</p>
                <label className="mt-2 inline-block cursor-pointer">
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => handleFile(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                  <span className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700">
                    Choose file
                  </span>
                </label>
                {file && (
                  <p className="mt-3 text-xs text-gray-700">
                    Selected: <span className="font-medium">{file.name}</span> ({Math.round(file.size / 1024)} KB)
                  </p>
                )}
              </div>
            )}

            {step === 2 && preview && (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800 flex items-start gap-2">
                  <AlertCircle size={14} className="mt-0.5" />
                  <span>
                    We auto-matched columns. Adjust any mapping below.
                    At least one column must map to <code className="bg-white px-1 rounded">phone</code>.
                  </span>
                </div>
                <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="px-3 py-2 text-left">CSV column</th>
                      <th className="px-3 py-2 text-left">Sample</th>
                      <th className="px-3 py-2 text-left">Map to</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.headers.map((h) => (
                      <tr key={h} className="border-t border-gray-100">
                        <td className="px-3 py-2 font-medium text-gray-900">{h}</td>
                        <td className="px-3 py-2 text-gray-600 text-xs truncate max-w-[200px]">
                          {preview.sample_rows?.[0]?.[h] || "—"}
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={mapping[h] || SKIP_TARGET}
                            onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })}
                            className="text-sm border border-gray-200 rounded px-2 py-1.5 bg-white w-full max-w-xs"
                          >
                            <option value={SKIP_TARGET}>Skip column</option>
                            <optgroup label="Default fields">
                              {targetOptions.defaults.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </optgroup>
                            {targetOptions.customs.length > 0 && (
                              <optgroup label="Custom fields">
                                {targetOptions.customs.map((o) => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </optgroup>
                            )}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 text-sm text-emerald-800">
                  We’re ready to start the import. Once queued, you can close this dialog —
                  processing happens in the background and the Imports tab shows live progress.
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-gray-500">File</p>
                    <p className="font-medium text-gray-900">{file?.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Mapped columns</p>
                    <p className="font-medium text-gray-900">
                      {Object.values(mapping).filter((v) => v !== SKIP_TARGET).length} / {preview?.headers?.length}
                    </p>
                  </div>
                </div>
                <label className="block">
                  <span className="text-xs font-medium text-gray-600">
                    Add imported contacts to a list (optional)
                  </span>
                  <select
                    value={listId}
                    onChange={(e) => setListId(e.target.value)}
                    className="mt-1 w-full text-sm border border-gray-200 rounded-md px-3 py-2 bg-white"
                  >
                    <option value="">Don't add to a list</option>
                    {lists.map((l) => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                </label>
              </div>
            )}
          </div>

          <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-between gap-2">
            <div>
              {step > 1 && (
                <button
                  onClick={() => setStep((s) => s - 1)}
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-gray-200 rounded-md bg-white hover:bg-gray-50"
                >
                  <ChevronLeft size={14} /> Back
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={onClose} className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md">Cancel</button>
              {step === 1 && (
                <button
                  onClick={runPreview}
                  disabled={!file || previewing}
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-60"
                >
                  {previewing ? "Reading…" : "Next"}
                  <ChevronRight size={14} />
                </button>
              )}
              {step === 2 && (
                <button
                  onClick={() => setStep(3)}
                  disabled={!phoneIsMapped}
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-60"
                >
                  Next <ChevronRight size={14} />
                </button>
              )}
              {step === 3 && (
                <button
                  onClick={submit}
                  disabled={submitting}
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-60"
                >
                  {submitting ? "Queueing…" : "Start import"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ImportWizard;
